// deployment_class: customer_env_allowed
/**
 * landingDrillData — the illustrative data + geometry behind the landing's two
 * live-demo drill-ins (E3 annotations 1a/1b/1c, 2026-08-01):
 *
 *   1. The FAN-OUT CONSTELLATION (LandingConstellation) — a dark radial
 *      hub-and-spoke graph. Every dot is a DATA POINT in the (illustrative)
 *      project; a headline number fans DOWN through the workflow/process that
 *      composes it to the granular task leaves (in-motion vs completed), and
 *      each dot traces back to the root. It is the "every value traces to its
 *      source · verify me" thesis made visual.
 *
 *   2. The APP COCKPIT (LandingAppDemo) — a light-glass "take me INTO the app"
 *      demo of a single partnership's workflow. Cross-links to the constellation.
 *
 * ALL names + figures are ILLUSTRATIVE and fictional (Harbor Point / Summit
 * Crossing / Meadow Glen / Park Terrace · Meridian / Halcyon / Kestrel · Lakeside
 * Housing Authority) — deliberately distinct from any stored data, because the
 * landing is a PUBLIC surface (E3 2026-08-01). The landing doubles as a LIVE demo,
 * so the data is coherent enough to explore end-to-end.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Shared workflow steps (single source — the landing stepper AND the cockpit
// both read these, so they never drift).
// ─────────────────────────────────────────────────────────────────────────────

export type StepState = 'done' | 'now' | 'todo';

export interface WfStep {
  /** Short label under the step dot. */
  l: string;
  /** Progress state — drives the stepper + cockpit rail styling. */
  st: StepState;
  /** The dot glyph (✓ for done, else the step number). */
  n: string;
  /** The spun-out detail shown on hover / in the cockpit focus panel. */
  detail: string;
}

export interface Workflow {
  /** Cockpit id this workflow drives (deal slug). */
  id: string;
  title: string;
  sub: string;
  steps: ReadonlyArray<WfStep>;
}

export const WORKFLOWS: ReadonlyArray<Workflow> = [
  {
    id: 'harbor-point',
    title: 'Sign the Harbor Point JV agreement',
    sub: 'LIHTC 9% · $62M · Aug 15 closing — one gate from signing.',
    steps: [
      { l: 'Term sheet', st: 'done', n: '✓', detail: 'Terms agreed with Meridian Partners — 84 units, 9% LIHTC, GP/LP split locked.' },
      { l: 'ED review', st: 'done', n: '✓', detail: 'Executive Director signed off two days ago. Cleared.' },
      { l: 'CFO gate', st: 'now', n: '3', detail: 'CFO confirmation is the last gate before signing — it blocks the Aug 15 close.' },
      { l: 'Signatures', st: 'todo', n: '4', detail: 'ED, CFO, and Developer-GP co-sign the joint-venture agreement.' },
      { l: 'PA lock-up', st: 'todo', n: '5', detail: 'An immutable v.PA snapshot is cut — the partnership terms are locked to source.' },
    ],
  },
  {
    id: 'summit-crossing',
    title: 'Summit Crossing bond issuance',
    sub: 'Bond · $54M · DSCR 1.18 — held at the binding gate.',
    steps: [
      { l: 'Underwriting', st: 'done', n: '✓', detail: 'Deal underwritten — 72 units on a tax-exempt bond structure.' },
      { l: 'DSCR gate', st: 'now', n: '2', detail: 'DSCR is 1.18, under your 1.20 floor — the binding gate holds until it clears.' },
      { l: 'Bond sizing', st: 'todo', n: '3', detail: 'Final bond amount set against the debt-service coverage.' },
      { l: 'Closing', st: 'todo', n: '4', detail: 'Bond closes and funds the first construction draw.' },
      { l: 'Stabilization', st: 'todo', n: '5', detail: 'Lease-up to stabilized occupancy and coverage.' },
    ],
  },
  {
    id: 'meadow-glen',
    title: 'Close the Meadow Glen financing gap',
    sub: 'LIHTC 4% · $33M · $1.5M gap — in pro-forma review.',
    steps: [
      { l: 'Pro-forma', st: 'now', n: '1', detail: 'Review surfaced a $1.5M gap — the working session is Jul 22.' },
      { l: 'Gap plan', st: 'todo', n: '2', detail: 'A deferred developer fee is modeled to close the gap.' },
      { l: 'Commitment', st: 'todo', n: '3', detail: 'Financing commitment secured against the revised pro-forma.' },
      { l: 'Closing', st: 'todo', n: '4', detail: '110 units, 4% LIHTC — construction closing.' },
      { l: 'Stabilization', st: 'todo', n: '5', detail: 'Lease-up and conversion to permanent financing.' },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// App cockpit ("take me INTO the app") — one per partnership.
// ─────────────────────────────────────────────────────────────────────────────

export type Tone = 'good' | 'watch' | 'bad';

export interface CockpitKpi {
  k: string;
  v: string;
  s: string;
  tone?: Tone;
}

export interface Cockpit {
  id: string;
  name: string;
  structure: string;
  dev: string;
  rag: [Tone, string];
  tdc: string;
  units: string;
  blurb: string;
  /** Constellation node this deal maps to (for the "fan out the data" cross-link). */
  node: string;
  kpis: ReadonlyArray<CockpitKpi>;
  steps: ReadonlyArray<WfStep>;
  /** Ask-Kathedral style sourced line for the cockpit. */
  ai: { a: string; src: string };
}

const wfSteps = (id: string): ReadonlyArray<WfStep> =>
  WORKFLOWS.find((w) => w.id === id)!.steps;

export const COCKPITS: Record<string, Cockpit> = {
  'harbor-point': {
    id: 'harbor-point',
    name: 'Harbor Point',
    structure: 'LIHTC 9% · Partnership · P7 of 9',
    dev: 'Meridian Partners',
    rag: ['watch', 'ED review → CFO gate'],
    tdc: '$62M',
    units: '84',
    blurb: 'One gate from signing. The Aug 15 close depends on the CFO confirmation.',
    node: 'pr-harbor',
    kpis: [
      { k: 'Total dev cost', v: '$62M', s: 'Equity $41M · Debt $21M' },
      { k: 'Units', v: '84', s: '9% LIHTC allocation' },
      { k: 'Close date', v: 'Aug 15', s: 'placed-in-service deadline', tone: 'watch' },
      { k: 'Deferred fee', v: '$1.4M', s: 'within tolerance' },
    ],
    steps: wfSteps('harbor-point'),
    ai: {
      a: "If the CFO doesn't confirm by Friday, the Aug 15 close slips to September — and the 9% allocation is at risk past the placed-in-service deadline.",
      src: 'the Harbor Point signing workflow',
    },
  },
  'summit-crossing': {
    id: 'summit-crossing',
    name: 'Summit Crossing',
    structure: 'Tax-exempt Bond · Partnership · P5',
    dev: 'Meridian Partners',
    rag: ['bad', 'binding gate · breached'],
    tdc: '$54M',
    units: '72',
    blurb: 'DSCR 1.18 is under the 1.20 floor. The binding gate holds until it clears.',
    node: 'pr-summit',
    kpis: [
      { k: 'DSCR', v: '1.18', s: 'floor 1.20', tone: 'bad' },
      { k: 'Total dev cost', v: '$54M', s: '72 units' },
      { k: 'Resize', v: '−$2.1M', s: '→ DSCR 1.21', tone: 'good' },
      { k: 'Gate', v: 'Held', s: 'binding · breached', tone: 'bad' },
    ],
    steps: wfSteps('summit-crossing'),
    ai: {
      a: 'At 1.18 the bond cannot close as sized. Resizing down $2.1M brings coverage to 1.21 and clears the gate.',
      src: 'the Summit Crossing snapshot',
    },
  },
  'meadow-glen': {
    id: 'meadow-glen',
    name: 'Meadow Glen',
    structure: 'LIHTC 4% · Partnership · P2',
    dev: 'Halcyon Development',
    rag: ['watch', 'pro-forma review'],
    tdc: '$33M',
    units: '110',
    blurb: 'A $1.5M gap surfaced in review. A deferred developer fee is modeled to close it.',
    node: 'pr-meadow',
    kpis: [
      { k: 'Total dev cost', v: '$33M', s: '110 units' },
      { k: 'Financing gap', v: '$1.5M', s: 'deferred fee covers it', tone: 'watch' },
      { k: 'Working session', v: 'Jul 22', s: 'gap plan review' },
      { k: 'Comparable closes', v: '2', s: 'same structure' },
    ],
    steps: wfSteps('meadow-glen'),
    ai: {
      a: 'Yes — a deferred developer fee covers the $1.5M gap without new subsidy. Two comparable deals closed the same way.',
      src: 'the Meadow Glen pro-forma',
    },
  },
  'park-terrace': {
    id: 'park-terrace',
    name: 'Park Terrace',
    structure: 'Acquisition · Partnership · P3',
    dev: 'Kestrel Group',
    rag: ['good', 'active · stabilizing'],
    tdc: '$41M',
    units: '60',
    blurb: 'Stabilizing through lease-up. Coverage is holding above plan.',
    node: 'pr-park',
    kpis: [
      { k: 'Total dev cost', v: '$41M', s: '60 units' },
      { k: 'DSCR', v: '1.34', s: 'above the 1.20 floor', tone: 'good' },
      { k: 'Lease-up', v: '82%', s: 'on pace to stabilized' },
      { k: 'Status', v: 'Active', s: 'phase 3 · stabilizing', tone: 'good' },
    ],
    steps: [
      { l: 'Acquisition', st: 'done', n: '✓', detail: 'Property acquired with Kestrel Group — 60 units under an acquisition structure.' },
      { l: 'Rehab', st: 'done', n: '✓', detail: 'Moderate rehab completed on schedule.' },
      { l: 'Lease-up', st: 'now', n: '3', detail: 'Lease-up at 82% and climbing toward stabilized occupancy.' },
      { l: 'Stabilized', st: 'todo', n: '4', detail: 'Reach stabilized occupancy and 1.20+ coverage.' },
      { l: 'Permanent', st: 'todo', n: '5', detail: 'Convert to permanent financing.' },
    ],
    ai: {
      a: 'Park Terrace is the healthiest deal in the portfolio — lease-up at 82% with 1.34 coverage, no open gates.',
      src: 'the Park Terrace snapshot',
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// The fan-out constellation — the whole (illustrative) project as a data web.
// Every node is a data point; the tree fans core → hub → process → task leaf.
// ─────────────────────────────────────────────────────────────────────────────

export type NodeStatus = 'done' | 'motion' | 'watch' | 'neutral';
export type NodeKind = 'core' | 'hub' | 'process' | 'task';

export interface RawNode {
  id: string;
  label: string;
  /** The data-point value (a headline figure), shown on the node + in the panel. */
  value?: string;
  /** What this data point IS / where it feeds — the provenance readout. */
  detail?: string;
  status?: NodeStatus;
  /** Hub glyph. */
  icon?: string;
  /** If set, this data point opens the app cockpit for that deal. */
  deal?: string;
  children?: ReadonlyArray<RawNode>;
}

export interface ConstellationDef {
  core: { id: string; label: string; sub: string };
  hubs: ReadonlyArray<RawNode>;
}

const CONSTELLATION: ConstellationDef = {
  core: { id: 'core', label: 'Lakeside Housing Authority', sub: '78% to the 2030 housing plan' },
  hubs: [
    {
      id: 'planning',
      label: 'Planning',
      icon: '◷',
      status: 'motion',
      detail: 'The strategic plan and its objectives — what the agency is steering toward.',
      children: [
        {
          id: 'p-obj',
          label: 'Objectives',
          value: '8 / 9',
          status: 'done',
          detail: 'Eight of nine strategic objectives on track.',
          children: [
            { id: 'p-obj-staff', label: 'Resident staffing', value: '1 qtr behind', status: 'watch', detail: 'The one objective off track — resident-services hiring a quarter behind plan.' },
            { id: 'p-obj-review', label: 'Next plan review', value: 'Jul 22', status: 'motion', detail: 'Quarterly strategic-plan review.' },
          ],
        },
        {
          id: 'p-pri',
          label: 'Strategic priorities',
          status: 'motion',
          detail: 'The three board priorities the plan rolls up to.',
          children: [
            { id: 'p-vibrant', label: 'Vibrant Supply', value: '71%', status: 'motion', detail: 'Housing-production priority — rolls up from units delivered across the pipeline.' },
            { id: 'p-govern', label: 'High-Perf Gov', value: '54%', status: 'motion', detail: 'Governance-performance priority — board cadence, compliance, and audit posture.' },
            { id: 'p-welfare', label: 'Resident Wellbeing', value: '42%', status: 'watch', detail: 'Resident-services priority — held back by the staffing gap.' },
          ],
        },
      ],
    },
    {
      id: 'projects',
      label: 'Projects',
      icon: '⌂',
      status: 'watch',
      detail: 'The active development pipeline — every partnership and what it produces.',
      children: [
        {
          id: 'pr-pipe',
          label: 'Pipeline fee income',
          value: '$478M',
          status: 'motion',
          detail: 'Total development cost across the ten active partnerships.',
          children: [
            { id: 'pr-harbor', label: 'Harbor Point', value: '$62M · 84u', status: 'motion', deal: 'harbor-point', detail: 'LIHTC 9% with Meridian Partners — one gate from signing.' },
            { id: 'pr-summit', label: 'Summit Crossing', value: '$54M · 72u', status: 'watch', deal: 'summit-crossing', detail: 'Tax-exempt bond — held at the binding DSCR gate.' },
            { id: 'pr-meadow', label: 'Meadow Glen', value: '$33M · 110u', status: 'watch', deal: 'meadow-glen', detail: 'LIHTC 4% with Halcyon — a $1.5M gap in review.' },
            { id: 'pr-park', label: 'Park Terrace', value: '$41M · 60u', status: 'done', deal: 'park-terrace', detail: 'Acquisition with Kestrel — active and stabilizing.' },
          ],
        },
        {
          id: 'pr-units',
          label: "Units delivered '26",
          value: '312',
          status: 'motion',
          detail: 'Units delivered this year, on pace against a 280 target.',
          children: [
            { id: 'pr-u-pace', label: 'On pace vs 280', value: '312', status: 'done', detail: 'Ahead of the annual delivery target.' },
            { id: 'pr-u-goal', label: '2030 housing goal', value: '1,780 / 1,820', status: 'motion', detail: '40 units short at current pace — Park Terrace phase 2 closes it.' },
          ],
        },
      ],
    },
    {
      id: 'oversight',
      label: 'Oversight',
      icon: '◉',
      status: 'done',
      detail: 'Department performance and compliance posture across the agency.',
      children: [
        {
          id: 'ov-dept',
          label: 'Departments on track',
          value: '11 / 14',
          status: 'motion',
          detail: 'Eleven of fourteen departments meeting their initiatives.',
          children: [
            { id: 'ov-init', label: 'Initiatives', value: '64%', status: 'motion', detail: 'Agency initiatives at 64% complete.' },
            { id: 'ov-behind', label: '3 depts behind', value: '3', status: 'watch', detail: 'Three departments trailing their targets.' },
          ],
        },
        {
          id: 'ov-comp',
          label: 'Compliance',
          value: '1 flag',
          status: 'watch',
          detail: 'One open compliance flag across the agency.',
          children: [
            { id: 'ov-reac', label: 'REAC review', value: 'Aug 30', status: 'watch', detail: 'REAC inspection window — the open flag; response can be pre-staged.' },
            { id: 'ov-mtw', label: 'MTW cert', value: 'cleared', status: 'done', detail: 'Moving-to-Work certification cleared in May.' },
          ],
        },
      ],
    },
    {
      id: 'board',
      label: 'Board',
      icon: '⚖',
      status: 'motion',
      detail: 'The board packet and the resolutions awaiting sign-off.',
      children: [
        {
          id: 'bd-packet',
          label: 'Packet assembled',
          value: '60%',
          status: 'motion',
          detail: 'The July board packet — three resolutions need sign-off.',
          children: [
            { id: 'bd-r14', label: 'R-14 · Harbor Point JV', status: 'motion', detail: 'Resolution R-2026-14 — approve the Harbor Point joint-venture agreement.' },
            { id: 'bd-r15', label: 'R-15 · Summit Crossing', status: 'watch', detail: 'Resolution R-2026-15 — hold for the resize, or approve with the coverage note.' },
            { id: 'bd-r16', label: 'R-16 · Procurement', status: 'done', detail: 'Resolution R-2026-16 — procurement, clean and ready.' },
          ],
        },
        {
          id: 'bd-quorum',
          label: 'Quorum',
          value: 'confirmed',
          status: 'done',
          detail: 'Quorum confirmed for the July meeting.',
          children: [
            { id: 'bd-meet', label: 'Board meeting · Jul 14', status: 'motion', detail: 'Next board meeting.' },
          ],
        },
      ],
    },
    {
      id: 'intelligence',
      label: 'Intelligence',
      icon: '✦',
      status: 'motion',
      detail: 'Ask Kathedral and the distribution ledger — the analytics layer.',
      children: [
        {
          id: 'in-ask',
          label: 'Ask Kathedral',
          status: 'done',
          detail: 'Every answer is sourced back to a snapshot — verify me.',
          children: [
            { id: 'in-fore', label: 'Forecasts', value: '4 active', status: 'motion', detail: 'Live forecasts across the four active partnerships.' },
            { id: 'in-src', label: 'Sourced answers', value: 'traced', status: 'done', detail: 'Each answer traces to the deal snapshot it came from.' },
          ],
        },
        {
          id: 'in-ledger',
          label: 'Distribution ledger',
          status: 'motion',
          detail: 'Cash position and distributions across the portfolio.',
          children: [
            { id: 'in-draws', label: 'Q3 draws overlap', value: 'Oct', status: 'watch', detail: 'Harbor Point and Meadow Glen draws overlap in October — the tight month.' },
            { id: 'in-fee', label: 'Deferred fee', value: '$4.2M', status: 'motion', detail: 'Deferred developer fee across the portfolio — within tolerance.' },
          ],
        },
      ],
    },
    {
      id: 'marketplace',
      label: 'Marketplace',
      icon: '⛓',
      status: 'done',
      detail: 'Verified developer partners and the provenance chains behind each deal.',
      children: [
        {
          id: 'mk-part',
          label: 'Verified partners',
          value: '3',
          status: 'done',
          detail: 'Three Kathedral-Verified developer partners in the portfolio.',
          children: [
            { id: 'mk-merid', label: 'Meridian Partners', status: 'done', detail: 'Verified — Harbor Point + Summit Crossing.' },
            { id: 'mk-halcyon', label: 'Halcyon Development', status: 'motion', detail: 'Verified — Meadow Glen, in active review.' },
            { id: 'mk-kestrel', label: 'Kestrel Group', status: 'done', detail: 'Verified — Park Terrace.' },
          ],
        },
        {
          id: 'mk-chain',
          label: 'Merkle chains',
          status: 'done',
          detail: 'Immutable snapshot chains — the source of truth for each deal.',
          children: [
            { id: 'mk-hp', label: 'Harbor Point chain', value: 'verified', status: 'done', deal: 'harbor-point', detail: 'Four-snapshot Merkle chain — verified end to end.' },
          ],
        },
      ],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Radial layout — pure + deterministic. Places the tree into STAGE-square coords.
// ─────────────────────────────────────────────────────────────────────────────

export const STAGE = 1160;
const CENTER = STAGE / 2; // 580
/** Ring radius by depth (core=0, hub=1, process=2, task=3+ clamp). Siblings get an
 * extra per-index radius stagger on top of these (see `place`) so labels never collide. */
const RADII = [0, 180, 330, 448];

export interface PlacedNode {
  id: string;
  label: string;
  value?: string;
  detail?: string;
  status: NodeStatus;
  kind: NodeKind;
  icon?: string;
  deal?: string;
  x: number;
  y: number;
  depth: number;
  parentId: string | null;
  angleDeg: number;
}

export interface PlacedEdge {
  id: string;
  from: string;
  to: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  depth: number;
}

export interface ConstellationLayout {
  nodes: ReadonlyArray<PlacedNode>;
  edges: ReadonlyArray<PlacedEdge>;
  /** id → node, for focus/ancestor lookups. */
  byId: Record<string, PlacedNode>;
  /** id → child ids. */
  childrenOf: Record<string, string[]>;
}

function kindFor(depth: number, hasChildren: boolean): NodeKind {
  if (depth === 0) return 'core';
  if (depth === 1) return 'hub';
  return hasChildren ? 'process' : 'task';
}

/** Deterministically place the whole constellation into STAGE×STAGE coords. */
export function layoutConstellation(): ConstellationLayout {
  const nodes: PlacedNode[] = [];
  const edges: PlacedEdge[] = [];
  const childrenOf: Record<string, string[]> = {};

  const place = (
    node: RawNode,
    angleDeg: number,
    spanDeg: number,
    depth: number,
    parentId: string | null,
    radiusBoost: number,
  ): void => {
    const r = RADII[Math.min(depth, RADII.length - 1)]! + radiusBoost;
    const a = (angleDeg * Math.PI) / 180;
    const x = CENTER + r * Math.cos(a);
    const y = CENTER + r * Math.sin(a);
    const kids = node.children ?? [];
    nodes.push({
      id: node.id,
      label: node.label,
      value: node.value,
      detail: node.detail,
      status: node.status ?? 'neutral',
      kind: kindFor(depth, kids.length > 0),
      icon: node.icon,
      deal: node.deal,
      x,
      y,
      depth,
      parentId,
      angleDeg,
    });
    if (parentId) {
      (childrenOf[parentId] ??= []).push(node.id);
    }
    if (kids.length === 0) return;
    // Spread children across the wedge centred on this node's angle; stagger each
    // sibling's radius (into up to 3 rings) so adjacent labels never collide.
    const usableSpan = kids.length === 1 ? spanDeg * 0.5 : spanDeg;
    kids.forEach((kid, i) => {
      const t = kids.length === 1 ? 0 : i / (kids.length - 1) - 0.5; // -0.5..0.5
      const childAngle = angleDeg + t * usableSpan;
      const childSpan = (usableSpan / Math.max(1, kids.length)) * 0.92;
      const boost = kids.length > 1 ? (i % 3) * 42 : 0;
      place(kid, childAngle, childSpan, depth + 1, node.id, boost);
    });
  };

  // Core, then hubs evenly around the full circle (top = -90°).
  const core = CONSTELLATION.core;
  nodes.push({
    id: core.id,
    label: core.label,
    detail: core.sub,
    status: 'neutral',
    kind: 'core',
    x: CENTER,
    y: CENTER,
    depth: 0,
    parentId: null,
    angleDeg: 0,
  });
  const n = CONSTELLATION.hubs.length;
  const hubWedge = 360 / n;
  CONSTELLATION.hubs.forEach((hub, i) => {
    const hubAngle = -90 + i * hubWedge;
    place(hub, hubAngle, hubWedge * 0.94, 1, core.id, 0);
  });

  const byId: Record<string, PlacedNode> = {};
  for (const nd of nodes) byId[nd.id] = nd;

  // Resolve edges from parent links now that every node has coords.
  for (const nd of nodes) {
    if (!nd.parentId) continue;
    const p = byId[nd.parentId]!;
    edges.push({
      id: `${nd.parentId}->${nd.id}`,
      from: nd.parentId,
      to: nd.id,
      x1: p.x,
      y1: p.y,
      x2: nd.x,
      y2: nd.y,
      depth: nd.depth,
    });
  }

  return { nodes, edges, byId, childrenOf };
}

/** Walk parent links from a node up to (and including) the core. */
export function ancestorPath(byId: Record<string, PlacedNode>, id: string): string[] {
  const chain: string[] = [];
  let cur: string | null = id;
  while (cur) {
    chain.push(cur);
    cur = byId[cur]?.parentId ?? null;
  }
  return chain;
}

/** All descendant ids of a node (inclusive), via the children map. */
export function subtreeIds(childrenOf: Record<string, string[]>, id: string): string[] {
  const out: string[] = [id];
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const c of childrenOf[cur] ?? []) {
      out.push(c);
      stack.push(c);
    }
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Navigable index for the RECURSIVE-ZOOM constellation (E3 2026-08-01 refinement):
// the drill is an in-place zoom that keeps diving — a deal fans into its workflow +
// metrics, a step fans into its snapshot source, alluding to limitless depth. This
// index resolves parent/child links over the tree, synthesizing the deeper levels.
// ─────────────────────────────────────────────────────────────────────────────

export interface NodeIndex {
  byId: Record<string, RawNode>;
  parentOf: Record<string, string | null>;
  childIdsOf: Record<string, string[]>;
}

/** Synthesize a deal's deeper levels: Workflow (→ steps → snapshot source) + Metrics. */
function dealChildren(dealId: string): RawNode[] {
  const c = COCKPITS[dealId];
  if (!c) return [];
  return [
    {
      id: `${dealId}::wf`,
      label: 'Workflow',
      status: 'motion',
      detail: `The ${c.name} workflow — the steps that make this deal what it is.`,
      children: c.steps.map((s, i) => ({
        id: `${dealId}::wf::${i}`,
        label: s.l,
        status: (s.st === 'done' ? 'done' : s.st === 'now' ? 'motion' : 'neutral') as NodeStatus,
        detail: s.detail,
        children: [
          {
            id: `${dealId}::wf::${i}::src`,
            label: 'Snapshot',
            value: '⛓ verified',
            status: 'done' as NodeStatus,
            detail:
              'This step is cut into an immutable v.PA snapshot — every value traces to source. Verify me.',
          },
        ],
      })),
    },
    {
      id: `${dealId}::kpi`,
      label: 'Metrics',
      status: 'neutral',
      detail: `The figures behind ${c.name}.`,
      children: c.kpis.map((k, i) => ({
        id: `${dealId}::kpi::${i}`,
        label: k.k,
        value: k.v,
        status: (k.tone === 'bad' ? 'watch' : k.tone === 'good' ? 'done' : 'motion') as NodeStatus,
        detail: `${k.k}: ${k.v} — ${k.s}.`,
      })),
    },
  ];
}

/** Build the parent/child index over the whole (synthesized) tree, rooted at 'core'. */
export function buildNodeIndex(): NodeIndex {
  const byId: Record<string, RawNode> = {};
  const parentOf: Record<string, string | null> = {};
  const childIdsOf: Record<string, string[]> = {};

  const root: RawNode = {
    id: CONSTELLATION.core.id,
    label: CONSTELLATION.core.label,
    detail: CONSTELLATION.core.sub,
    children: CONSTELLATION.hubs,
  };

  const walk = (node: RawNode, parent: string | null): void => {
    byId[node.id] = node;
    parentOf[node.id] = parent;
    let kids: ReadonlyArray<RawNode> = node.children ?? [];
    if (node.deal && kids.length === 0) kids = dealChildren(node.deal);
    childIdsOf[node.id] = kids.map((k) => k.id);
    for (const k of kids) walk(k, node.id);
  };
  walk(root, null);
  return { byId, parentOf, childIdsOf };
}

/** Path of ids from the root down to (and including) `id`. */
function pathTo(parentOf: Record<string, string | null>, id: string): string[] {
  const chain: string[] = [];
  let cur: string | null = id;
  while (cur) {
    chain.unshift(cur);
    cur = parentOf[cur] ?? null;
  }
  return chain;
}

// ─────────────────────────────────────────────────────────────────────────────
// Recursive-zoom layout: lay out the FULL subtree rooted at `focusId` (bounded
// depth, with a "teased" flag where deeper children exist but are held back). At
// focusId='core' this reproduces the loved full ~46-node web exactly; zooming into
// a node re-roots the same algorithm on that node → its own rich sub-web.
// ─────────────────────────────────────────────────────────────────────────────

/** How many rings below the focus we render before teasing the rest (the horizon). */
const FOCUS_MAX_DEPTH = 3;

export interface FocusPlacedNode {
  id: string;
  label: string;
  value?: string;
  detail?: string;
  status: NodeStatus;
  kind: NodeKind;
  icon?: string;
  x: number;
  y: number;
  depth: number;
  parentId: string | null;
  /** Has children beyond the rendered depth — implies "more inside" (zoom to reveal). */
  teased: boolean;
  kidCount: number;
}

export interface FocusPlacedEdge {
  id: string;
  from: string;
  to: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  depth: number;
}

export interface FocusLayout {
  nodes: ReadonlyArray<FocusPlacedNode>;
  edges: ReadonlyArray<FocusPlacedEdge>;
}

export function layoutFocus(index: NodeIndex, focusId: string): FocusLayout {
  const { byId, childIdsOf } = index;
  const C = STAGE / 2;
  const nodes: FocusPlacedNode[] = [];
  const pos: Record<string, { x: number; y: number }> = {};

  const place = (
    id: string,
    angleDeg: number,
    spanDeg: number,
    depth: number,
    parentId: string | null,
    boost: number,
  ): void => {
    const node = byId[id];
    if (!node) return;
    const kidIds = childIdsOf[id] ?? [];
    const r = (RADII[Math.min(depth, RADII.length - 1)] ?? 0) + boost;
    const a = (angleDeg * Math.PI) / 180;
    const x = C + r * Math.cos(a);
    const y = C + r * Math.sin(a);
    pos[id] = { x, y };
    const atBound = depth >= FOCUS_MAX_DEPTH;
    const rendersKids = kidIds.length > 0 && !atBound;
    const kind: NodeKind = depth === 0 ? 'core' : depth === 1 ? 'hub' : rendersKids ? 'process' : 'task';
    nodes.push({
      id,
      label: node.label,
      value: node.value,
      detail: node.detail,
      status: node.status ?? 'neutral',
      kind,
      icon: node.icon,
      x,
      y,
      depth,
      parentId,
      teased: atBound && kidIds.length > 0,
      kidCount: kidIds.length,
    });
    if (!rendersKids) return;
    const usable = kidIds.length === 1 ? spanDeg * 0.5 : spanDeg;
    kidIds.forEach((kid, i) => {
      const t = kidIds.length === 1 ? 0 : i / (kidIds.length - 1) - 0.5;
      const childAngle = angleDeg + t * usable;
      const childSpan = (usable / Math.max(1, kidIds.length)) * 0.92;
      const b = kidIds.length > 1 ? (i % 3) * 42 : 0;
      place(kid, childAngle, childSpan, depth + 1, id, b);
    });
  };

  // Focus at the center; its children fan the full circle (like the hubs at root).
  const fnode = byId[focusId] ?? byId['core']!;
  const fid = byId[focusId] ? focusId : 'core';
  pos[fid] = { x: C, y: C };
  const fkids = childIdsOf[fid] ?? [];
  nodes.push({
    id: fid,
    label: fnode.label,
    value: fnode.value,
    detail: fnode.detail,
    status: fnode.status ?? 'neutral',
    kind: 'core',
    icon: fnode.icon,
    x: C,
    y: C,
    depth: 0,
    parentId: null,
    teased: false,
    kidCount: fkids.length,
  });
  const n = fkids.length;
  const wedge = 360 / Math.max(1, n);
  fkids.forEach((kid, i) => {
    place(kid, -90 + i * wedge, wedge * 0.94, 1, fid, 0);
  });

  const edges: FocusPlacedEdge[] = [];
  for (const nd of nodes) {
    if (!nd.parentId) continue;
    const p = pos[nd.parentId];
    if (!p) continue;
    edges.push({ id: `${nd.parentId}->${nd.id}`, from: nd.parentId, to: nd.id, x1: p.x, y1: p.y, x2: nd.x, y2: nd.y, depth: nd.depth });
  }
  return { nodes, edges };
}

// ─────────────────────────────────────────────────────────────────────────────
// AUTO-PLAY CINEMATIC (E3 2026-08-01 · "we DO NOT want to lose anyone"):
// the constellation is no longer USER-driven exploration — it is an art-directed,
// self-running reel. `buildCinematic` scripts a TIMELINE of camera beats that
// drive the SAME zoom/re-root engine (`layoutFocus` + focus/lit state) the loved
// interactive view used, so the visual is identical — only the driver changes.
//
// Every reel follows the same 5-act arc: open (the headline) → dive (travel the
// lineage down) → proof (land on the crux · traces to source · verify me) →
// bloom (pull back · the whole web lights up) → gate (the account climax — "see
// it live with your agency's real data"). The flagship story is Summit Crossing
// held at the binding DSCR gate; every OTHER surface plays a contextual reel of
// the same arc, focused on ITS own data. The user never operates anything.
// ─────────────────────────────────────────────────────────────────────────────

export type CineStage = 'open' | 'dive' | 'proof' | 'advisory' | 'bloom' | 'gate';

export interface CineBeat {
  stage: CineStage;
  /** Node the camera re-roots on — drives layoutFocus + the zoom continuity transition. */
  focus: string;
  /** Lineage node to light (null → the whole web blooms bright, e.g. the bloom beat). */
  lit: string | null;
  /** Direction the re-root should read as (dive = in; pull-back = out). */
  dir: 'in' | 'out';
  /** Lower-third overlay copy for this beat (crossfades between beats). */
  eyebrow: string;
  title: string;
  sub?: string;
  /** Surface the ⛓ traces-to-source proof chip prominently — the crux moment. */
  proof?: boolean;
  /** The AI/Advisory takeover payload — Kathedral reads the crux and hands the decision. */
  advisory?: { rec: string; src: string };
  /** ms to hold before auto-advancing; null = terminal beat (the account gate). */
  hold: number | null;
}

/** Beat hold durations (ms). Marketing/explanatory motion can run longer than UI. */
const CINE_HOLD = { open: 2600, dive: 2050, proof: 3200, advisory: 4400, bloom: 2900 } as const;

/**
 * The crux each deal reel lands on — a real, notable data point + its "verify me"
 * framing AND the grounded advisory Kathedral hands back (the AI-takeover payoff:
 * it reads the data → recommends the decision → sources it). Summit is the flagship
 * (the binding DSCR gate); the others give every deal surface its own crux + advice.
 */
const CINE_CRUX: Record<
  string,
  { node: string; title: string; sub: string; rec: string; src: string }
> = {
  'pr-summit': {
    node: 'summit-crossing::kpi::0',
    title: 'DSCR 1.18 vs the 1.20 floor',
    sub: 'Every figure traces to its source.',
    rec: 'Resize the bond −$2.1M to bring coverage to 1.21 and clear the gate — or approve with a coverage note for the board.',
    src: 'the Summit Crossing snapshot',
  },
  'pr-harbor': {
    node: 'harbor-point::kpi::2',
    title: 'Aug 15 close hinges on the CFO gate',
    sub: 'One confirmation from signing — traced to source.',
    rec: 'Lock the CFO confirmation by Friday. A slip past Aug 15 risks the 9% allocation past the placed-in-service deadline.',
    src: 'the Harbor Point signing workflow',
  },
  'pr-meadow': {
    node: 'meadow-glen::kpi::1',
    title: 'A $1.5M gap, closed by a deferred fee',
    sub: 'Two comparable deals closed the same way.',
    rec: 'Close the $1.5M gap with a deferred developer fee — no new subsidy required. Two comparable deals closed the same way.',
    src: 'the Meadow Glen pro-forma',
  },
  'pr-park': {
    node: 'park-terrace::kpi::1',
    title: '1.34 DSCR, above the 1.20 floor',
    sub: 'The healthiest deal in the book — traced to source.',
    rec: 'Hold course — coverage at 1.34 with lease-up at 82%. No gate action needed; on pace to stabilized.',
    src: 'the Park Terrace snapshot',
  },
};

/** The account-gate climax copy — the conversion moment: get this AI advisory live. */
const CINE_GATE = {
  eyebrow: 'This is a glimpse',
  title: 'Get this advisory live, on your agency’s real data',
  sub: 'Kathedral reads every deal and hands you the decision — provably, traced to source. Create your account.',
};

/** Surfaces that ride the flagship Summit story (anything on the path to Summit). */
function ridesSummitStory(id: string): boolean {
  return (
    id === 'core' ||
    id === 'projects' ||
    id === 'pr-pipe' ||
    id === 'pr-summit' ||
    id.startsWith('summit-crossing')
  );
}

/** Pick the most notable child to spotlight (needs-attention first, then in-motion). */
function pickNotable(index: NodeIndex, ids: ReadonlyArray<string>): string {
  const rank = (s?: NodeStatus): number =>
    s === 'watch' ? 0 : s === 'motion' ? 1 : s === 'done' ? 2 : 3;
  let best = ids[0] ?? 'core';
  let bestRank = 99;
  for (const id of ids) {
    const r = rank(index.byId[id]?.status);
    if (r < bestRank) {
      bestRank = r;
      best = id;
    }
  }
  return best;
}

/**
 * Script the auto-play reel for a clicked surface (or the hero Play ▶, which opens
 * on 'core'). Reuses the parent/child index so the camera travels REAL lineage.
 */
export function buildCinematic(
  index: NodeIndex,
  focusId: string,
  originLabel: string,
  originSub: string,
): CineBeat[] {
  const { byId, parentOf, childIdsOf } = index;
  const target = byId[focusId] ? focusId : 'core';

  const bloom: CineBeat = {
    stage: 'bloom',
    focus: 'core',
    lit: null,
    dir: 'out',
    eyebrow: 'One anchor',
    title: 'Every deal. Every value. Traced to source.',
    sub: 'This is a glimpse of the whole agency.',
    hold: CINE_HOLD.bloom,
  };
  const gate: CineBeat = {
    stage: 'gate',
    focus: 'core',
    lit: null,
    dir: 'out',
    eyebrow: CINE_GATE.eyebrow,
    title: CINE_GATE.title,
    sub: CINE_GATE.sub,
    hold: null,
  };

  // ── Flagship marquee: the Summit Crossing binding-gate story ──
  if (ridesSummitStory(target)) {
    const crux = CINE_CRUX['pr-summit']!;
    return [
      {
        stage: 'open',
        focus: 'core',
        lit: 'pr-summit',
        dir: 'in',
        eyebrow: 'Lakeside Housing Authority · live',
        title: originLabel,
        sub: originSub,
        hold: CINE_HOLD.open,
      },
      {
        stage: 'dive',
        focus: 'projects',
        lit: 'pr-summit',
        dir: 'in',
        eyebrow: 'Follow the lineage',
        title: '$478M pipeline · 10 partnerships',
        sub: 'One deal is held at a gate.',
        hold: CINE_HOLD.dive,
      },
      {
        stage: 'dive',
        focus: 'pr-summit',
        lit: null,
        dir: 'in',
        eyebrow: 'Summit Crossing',
        title: '$54M tax-exempt bond',
        sub: 'Held at the binding DSCR gate.',
        hold: CINE_HOLD.dive,
      },
      {
        stage: 'proof',
        focus: 'pr-summit',
        lit: crux.node,
        dir: 'in',
        eyebrow: 'The crux',
        title: crux.title,
        sub: crux.sub,
        proof: true,
        hold: CINE_HOLD.proof,
      },
      {
        stage: 'advisory',
        focus: 'pr-summit',
        lit: crux.node,
        dir: 'in',
        eyebrow: 'Kathedral · Advisory',
        title: 'Kathedral hands you the decision',
        sub: 'The AI reads the data and recommends the move.',
        advisory: { rec: crux.rec, src: crux.src },
        hold: CINE_HOLD.advisory,
      },
      bloom,
      gate,
    ];
  }

  // ── Contextual reel: dive to the clicked surface's OWN data + crux ──
  const chain = pathTo(parentOf, target); // ['core', hub, …, target]
  const hub = chain[1] ?? target;
  const kids = childIdsOf[target] ?? [];
  const override = CINE_CRUX[target];

  let proofFocus: string;
  let proofNodeId: string;
  if (override) {
    proofFocus = target;
    proofNodeId = override.node;
  } else if (kids.length > 0) {
    proofFocus = target;
    proofNodeId = pickNotable(index, kids);
  } else {
    proofFocus = parentOf[target] ?? target;
    proofNodeId = target;
  }

  // Ordered, de-duped dive stops from the overview down to the proof view.
  const stops: string[] = [];
  for (const id of ['core', hub, proofFocus]) {
    if (!stops.includes(id)) stops.push(id);
  }

  const proofNode = byId[proofNodeId];
  const proofTitle = override
    ? override.title
    : proofNode?.value
      ? `${proofNode.label} · ${proofNode.value}`
      : (proofNode?.label ?? 'Traced to source');
  const proofSub = override
    ? override.sub
    : proofNode?.detail
      ? proofNode.detail
      : 'Every value traces to its source.';

  const beats: CineBeat[] = [];
  beats.push({
    stage: 'open',
    focus: 'core',
    lit: proofNodeId,
    dir: 'in',
    eyebrow: 'Lakeside Housing Authority · live',
    title: originLabel,
    sub: originSub,
    hold: CINE_HOLD.open,
  });
  for (const id of stops.slice(1)) {
    const n = byId[id];
    beats.push({
      stage: 'dive',
      focus: id,
      lit: id === proofFocus ? null : proofNodeId,
      dir: 'in',
      eyebrow: 'Tracing the lineage',
      title: n?.value ? `${n.label} · ${n.value}` : (n?.label ?? ''),
      sub: n?.detail,
      hold: CINE_HOLD.dive,
    });
  }
  beats.push({
    stage: 'proof',
    focus: proofFocus,
    lit: proofNodeId,
    dir: 'in',
    eyebrow: 'The crux',
    title: proofTitle,
    sub: proofSub,
    proof: true,
    hold: CINE_HOLD.proof,
  });
  const advisory = override
    ? { rec: override.rec, src: override.src }
    : {
        rec: `Kathedral flags ${proofNode?.label ?? 'this'} and recommends the next step — reviewed against your plan and traced to source.`,
        src: byId[hub]?.label ? `the ${byId[hub]!.label} stream` : 'the source snapshot',
      };
  beats.push({
    stage: 'advisory',
    focus: proofFocus,
    lit: proofNodeId,
    dir: 'in',
    eyebrow: 'Kathedral · Advisory',
    title: 'Kathedral hands you the decision',
    sub: 'The AI reads the data and recommends the move.',
    advisory,
    hold: CINE_HOLD.advisory,
  });
  beats.push(bloom);
  beats.push(gate);
  return beats;
}
